import React, { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { Toaster } from "sonner";
import Navigation from "./components/site/Navigation";
import ParticleCursor from "./components/site/ParticleCursor";
import Hero from "./components/site/Hero";
import FounderStripe from "./components/site/FounderStripe";
import OrientationResetBridge from "./components/site/OrientationResetBridge";
import ShowcaseTeaser from "./components/site/ShowcaseTeaser";
import TestimonialsCarouselPremium from "./components/site/TestimonialsCarouselPremium";
import JournalTeaser from "./components/site/JournalTeaser";
import SelfIdentification from "./components/site/SelfIdentification";
import NotLostUnstructured from "./components/site/NotLostUnstructured";
import StructuredMomentumStrip from "./components/site/StructuredMomentumStrip";
import WorkWithMe from "./components/site/WorkWithMe";
import BookPhilosophy from "./components/site/BookPhilosophy";
import ProofOfMomentumTeaser from "./components/site/ProofOfMomentumTeaser";
import WhatAreYouHearing from "./components/site/WhatAreYouHearing";
import FloatingReflectionTrigger from "./components/site/FloatingReflectionTrigger";
import CopyFeedbackForm from "./components/site/CopyFeedbackForm";
import FinalCTA from "./components/site/FinalCTA";
import BookCheckoutHost from "./components/site/BookCheckoutHost";
import BlueprintCheckoutHost from "./components/site/BlueprintCheckoutHost";
import OrientationResetCheckoutHost from "./components/site/OrientationResetCheckoutHost";
import Footer from "./components/site/Footer";
import StickyMobileCTA from "./components/site/StickyMobileCTA";
import CheckoutSuccess from "./components/site/CheckoutSuccess";
import CheckoutCancel from "./components/site/CheckoutCancel";
import ConsultationModal from "./components/site/ui/ConsultationModal";
import CookieNotice from "./components/site/ui/CookieNotice";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { ConsultationProvider } from "./context/ConsultationContext";
import AboutPage from "./pages/AboutPage";
import EcosystemPage from "./pages/EcosystemPage";
import BlueprintStartPage from "./pages/BlueprintStartPage";
import BlueprintIntakePage from "./pages/BlueprintIntakePage";
import BlueprintPreviewPage from "./pages/BlueprintPreviewPage";
import FrequencySnapshotPage from "./pages/FrequencySnapshotPage";
import JournalIndexPage from "./pages/JournalIndexPage";
import JournalPostPage from "./pages/JournalPostPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import OrientationResetSchedulePage from "./pages/OrientationResetSchedulePage";
import StartHerePage from "./pages/StartHerePage";
import ProofPage from "./pages/ProofPage";
import VoicesPage from "./pages/VoicesPage";
import LinkedInLandingPage from "./pages/LinkedInLandingPage";
import BookPage from "./pages/BookPage";
import CyrusProfilePage from "./pages/CyrusProfilePage";
import FrameworkPage from "./pages/FrameworkPage";
import PressPage from "./pages/PressPage";
import ConnectPage from "./pages/ConnectPage";
import { captureUtm } from "./lib/utm";

// Code-split the heaviest route. Admin pulls a large dashboard tree
// (charts, multiple tabs, opportunity CRM) that public visitors never
// see; lazy() keeps it out of the initial bundle.
const AdminPage = lazy(() => import("./pages/AdminPage"));

// Stable reference — avoids creating a new object on every render.
const TOASTER_OPTIONS = {
  style: {
    background: "rgba(15,15,15,0.92)",
    border: "1px solid rgba(212,175,55,0.3)",
    color: "#F8F8F8",
    backdropFilter: "blur(14px)",
  },
};

const useHashScroll = () => {
  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash.replace("#", "");
      if (!hash) return;
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    // After initial render
    const t = setTimeout(scrollToHash, 80);
    window.addEventListener("hashchange", scrollToHash);
    return () => {
      clearTimeout(t);
      window.removeEventListener("hashchange", scrollToHash);
    };
  }, []);
};

// Allow any incoming link to auto-open a checkout modal once the homepage
// has mounted. Used by the Snapshot result page so "Start the Blueprint"
// drops the visitor straight into the Blueprint checkout instead of just
// scrolling to the section. Supports ?checkout=blueprint|orientation_reset.
const useCheckoutAutoOpen = () => {
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const target = params.get("checkout");
      if (!target) return;
      // Defer until BlueprintSection / OrientationResetCheckoutHost listeners attach.
      const t = setTimeout(() => {
        if (target === "blueprint") {
          window.dispatchEvent(new CustomEvent("am:open-blueprint-checkout"));
        } else if (target === "orientation_reset") {
          window.dispatchEvent(new CustomEvent("am:open-orientation-reset-checkout"));
        } else if (target === "book") {
          // The book is now sold via Amazon. Any legacy `?checkout=book`
          // link opens the Amazon listing in a new tab.
          import("./lib/externalLinks").then(({ AMAZON_BOOK_URL }) => {
            window.open(AMAZON_BOOK_URL, "_blank", "noopener,noreferrer");
          });
        }
        // Strip the param so refresh / share links stay clean.
        const url = new URL(window.location.href);
        url.searchParams.delete("checkout");
        window.history.replaceState({}, "", url.pathname + url.hash);
      }, 350);
      return () => clearTimeout(t);
    } catch {
      // no-op
    }
  }, []);
};

import { recordSectionView, getSignalSnapshot } from "./lib/signalPathEngine";
import { getConversionState } from "./lib/conversionState";
import { api } from "./lib/api";
import { captureAttribution } from "./lib/attribution";
import NotFoundPage from "./pages/NotFoundPage";

const TRACKED_SECTIONS = ["what-it-helps", "pathway", "orientation-reset-bridge", "showcase-teaser", "wisdom", "journal-teaser", "final-cta"];

// Lightweight homepage section wrapping the "Help Shape the Message"
// feedback form. Lives below the journal teaser so visitors who scroll
// the whole funnel can flag any copy that read as unclear before they
// land on the final CTA. Kept intentionally small to not compete with
// the conversion CTAs above it.
const CopyFeedbackSection = () => (
  <section
    id="help-shape-the-message"
    data-testid="help-shape-the-message-section"
    className="relative py-24 md:py-28 border-t border-gold/10"
  >
    <div className="max-w-3xl mx-auto px-6 md:px-10">
      <CopyFeedbackForm testidPrefix="homepage-copy-feedback" />
    </div>
  </section>
);

const CIRCULAR_SESSION_KEY = "am_circular_session_id";
const CIRCULAR_LAST_PING_KEY = "am_circular_last_ping";

// One stable random id per visitor so the backend can dedupe.
const getOrMintSession = () => {
  try {
    let id = localStorage.getItem(CIRCULAR_SESSION_KEY);
    if (!id) {
      id = `cs_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(CIRCULAR_SESSION_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
};

// Re-evaluate after every section impression. When the engine flags
// circular behavior AND we haven't pinged the backend in the last hour,
// push a single anonymous detection. Never sends quiz answers.
const maybePingCircular = () => {
  try {
    const snap = getSignalSnapshot();
    if (!snap.circular) return;
    const lastPing = parseInt(localStorage.getItem(CIRCULAR_LAST_PING_KEY) || "0", 10);
    if (Date.now() - lastPing < 60 * 60 * 1000) return;
    const session_id = getOrMintSession();
    if (!session_id) return;
    const conv = getConversionState();
    api.post("/circular-signal", {
      session_id,
      email: (conv.snapshotResult && conv.snapshotResult.email) || null,
      archetype: conv.snapshotResult?.archetype || null,
      reason: snap.circular.reason,
      strength: snap.circular.strength,
      sections_orbited: snap.circular.sections_orbited,
      identity_tag_reads: snap.circular.identity_tag_reads,
      distinct_journal_reads: snap.circular.distinct_journal_reads,
    }).catch(() => {});
    localStorage.setItem(CIRCULAR_LAST_PING_KEY, String(Date.now()));
  } catch {
    /* never block the page on telemetry */
  }
};

// Quietly logs which major homepage sections come into view, so the
// Signal Path Engine can score "browsing intent" without ever tracking
// scroll depth or behavioral analytics. localStorage only, no server
// ping. Each section only counts once per page load.
const useSectionImpressions = () => {
  React.useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const seen = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !seen.has(e.target.id)) {
            seen.add(e.target.id);
            recordSectionView(e.target.id);
            // After each new section view, re-evaluate circular signal.
            maybePingCircular();
          }
        });
      },
      { threshold: 0.35 }
    );
    TRACKED_SECTIONS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
};

const Landing = () => {
  useHashScroll();
  useCheckoutAutoOpen();
  useSectionImpressions();
  return (
    <>
      <ParticleCursor />
      <Navigation />
      <main>
        <Hero />
        <FounderStripe />
        <SelfIdentification />
        <NotLostUnstructured />
        <StructuredMomentumStrip />
        <WorkWithMe />
        <BookPhilosophy />
        <ProofOfMomentumTeaser />
        <WhatAreYouHearing />
        <OrientationResetBridge />
        <ShowcaseTeaser />
        <TestimonialsCarouselPremium />
        <JournalTeaser />
        <FinalCTA />
      </main>
      <Footer />
      <StickyMobileCTA />
      <FloatingReflectionTrigger />
      <BookCheckoutHost />
      <BlueprintCheckoutHost />
      <OrientationResetCheckoutHost />
    </>
  );
};

const NOINDEX_ROUTES = ["/admin", "/blueprint/", "/checkout/", "/frequency-snapshot"];

const ensureNoindex = (path) => {
  const shouldHide = NOINDEX_ROUTES.some((p) => path.startsWith(p));
  const existing = document.querySelector('meta[name="robots"][data-am-route="1"]');
  if (existing) existing.remove();
  if (shouldHide) {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex,nofollow";
    m.setAttribute("data-am-route", "1");
    document.head.appendChild(m);
  }
};

const KNOWN_ROUTE_PREFIXES = [
  "/checkout/success",
  "/checkout/cancel",
  "/blueprint/start",
  "/blueprint/intake",
  "/blueprint/preview",
  "/admin",
  "/frequency-snapshot",
  "/journal",
  "/about",
  "/cyrus-prescod",
  "/framework",
  "/press",
  "/connect",
  "/ecosystem",
  "/proof",
  "/voices",
  "/privacy",
  "/terms",
  "/orientation-reset",
  "/start-here",
  "/linkedin",
  "/book",
];
const isKnownRoute = (path) => {
  if (path === "/" || path === "") return true;
  return KNOWN_ROUTE_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`));
};

const resolveView = () => {
  const path = window.location.pathname;
  ensureNoindex(path);
  if (path.startsWith("/checkout/success")) return <CheckoutSuccess />;
  if (path.startsWith("/checkout/cancel")) return <CheckoutCancel />;
  if (path.startsWith("/blueprint/start")) return <BlueprintStartPage />;
  if (path.startsWith("/blueprint/intake")) return <BlueprintIntakePage />;
  if (path.startsWith("/blueprint/preview")) return <BlueprintPreviewPage />;
  if (path.startsWith("/admin"))
    return (
      <Suspense fallback={<div className="min-h-screen bg-black" />}>
        <AdminPage />
      </Suspense>
    );
  if (path.startsWith("/frequency-snapshot")) return <FrequencySnapshotPage />;
  if (path.startsWith("/privacy")) return <PrivacyPage />;
  if (path.startsWith("/terms")) return <TermsPage />;
  if (path.startsWith("/orientation-reset/schedule")) return <OrientationResetSchedulePage />;
  if (path === "/start-here" || path.startsWith("/start-here/")) return <StartHerePage />;
  if (path.startsWith("/journal/")) {
    const slug = path.replace(/^\/journal\//, "").replace(/\/$/, "");
    if (slug) return <JournalPostPage slug={slug} />;
  }
  if (path === "/journal" || path === "/journal/") return <JournalIndexPage />;
  if (path.startsWith("/cyrus-prescod")) return <CyrusProfilePage />;
  if (path.startsWith("/framework")) return <FrameworkPage />;
  if (path.startsWith("/press")) return <PressPage />;
  if (path.startsWith("/connect")) return <ConnectPage />;
  if (path.startsWith("/about")) return <AboutPage />;
  if (path.startsWith("/ecosystem")) return <EcosystemPage />;
  if (path.startsWith("/proof")) return <ProofPage />;
  if (path.startsWith("/voices")) return <VoicesPage />;
  if (path === "/linkedin" || path.startsWith("/linkedin/") || path === "/start-here/linkedin") return <LinkedInLandingPage />;
  if (path === "/book" || path.startsWith("/book/")) return <BookPage />;
  if (path === "/" || path === "") return <Landing />;
  if (!isKnownRoute(path)) return <NotFoundPage />;
  return <Landing />;
};

const AppShell = () => {
  const { theme } = useTheme();
  // Capture src / ref / utm_* from the landing URL exactly once per
  // mount. Persists to localStorage so the same attribution flows
  // into Stripe metadata, consultation source_context, and the
  // Opportunity Signals admin queue.
  useEffect(() => {
    captureAttribution();
    captureUtm();
  }, []);
  return (
    <div className="App" data-testid="app-root" data-app-theme={theme}>
      {resolveView()}
      <ConsultationModal />
      <CookieNotice />
      <Toaster
        theme={theme === "light" ? "light" : "dark"}
        position="bottom-right"
        toastOptions={TOASTER_OPTIONS}
      />
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <ConsultationProvider>
        <AppShell />
      </ConsultationProvider>
    </ThemeProvider>
  );
}

export default App;
